import React, { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import Seo from "../../components/Seo";
import { PageHeader, Spinner, EmptyState } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, lawyerService } from "../../services/lawyerApi";

const MAX_ANSWER = 5000;
const fileNameOf = (url) => decodeURIComponent((url || "").split("/").pop() || "Attachment");

const LawyerQuestionAnswer = () => {
  const { questionId } = useParams();
  const { refreshProfile } = useOutletContext();
  const basePath = process.env.REACT_APP_BASE_PATH || "";

  const [question, setQuestion] = useState(null);
  const [myAnswer, setMyAnswer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [answer, setAnswer] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [drafting, setDrafting] = useState(false);

  useEffect(() => {
    let active = true;
    lawyerService
      .getQuestionDetails(questionId)
      .then((res) => {
        if (!active) return;
        if (!res?.data?.status) throw new Error(res?.data?.message || "Question not found.");
        setQuestion(res.data.data.question);
        // The API returns { lawyer } even when unanswered, so check the answer text itself.
        setMyAnswer(res.data.data.my_answer?.answer ? res.data.data.my_answer : null);
        refreshProfile(); // opening a question marks it viewed — refresh the unread badge
      })
      .catch((err) => active && setError(apiErrorMessage(err, err?.message)))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [questionId, refreshProfile]);

  const writeWithAI = async () => {
    setDrafting(true);
    try {
      const res = await lawyerService.draftAnswerWithAI(question.id, question.question);
      const suggestion = res?.data?.data?.suggestion;
      if (!res?.data?.status || !suggestion) throw new Error(res?.data?.message);
      setAnswer(suggestion);
      toast.success("Draft ready. Review and edit it before submitting.");
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "The AI assistant is unavailable right now."));
    } finally {
      setDrafting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = answer.trim();
    if (!body) return toast.error("Please write your answer.");
    if (!accepted) return toast.error("Please accept the Privacy policy and Terms & conditions.");
    setSubmitting(true);
    try {
      const res = await lawyerService.answerQuestion(question.id, body);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      toast.success(res.data.message || "Answer submitted");
      setMyAnswer(res.data.data || { answer: body, created_at: new Date().toISOString() });
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to submit your answer."));
    } finally {
      setSubmitting(false);
    }
    return undefined;
  };

  const backTo = "/lawyer/requests?tab=questions";

  if (loading) {
    return (
      <div className="lwp-page lwp-page-narrow">
        <PageHeader title="My Answer" backTo={backTo} />
        <Spinner />
      </div>
    );
  }

  if (error || !question) {
    return (
      <div className="lwp-page lwp-page-narrow">
        <PageHeader title="My Answer" backTo={backTo} />
        <EmptyState icon="bi-question-circle" text={error || "Question not found."} />
      </div>
    );
  }

  const closed = question.status === "Closed";
  const attachments = Array.isArray(question.attachments) ? question.attachments : [];
  const when = moment(question.created_at);

  return (
    <div className="lwp-page lwp-page-narrow">
      <Seo title={`Question #${question.id} | Legal Platform`} noindex />
      <PageHeader title="My Answer" backTo={backTo} />

      <section className="lwp-question-card">
        <div className="lwp-question-meta">
          <span className="lwp-id-pill light">#{question.id}</span>
          {question.jurisdiction?.name && <span>{question.jurisdiction.name}</span>}
          {when.isValid() && <span>{when.format("DD MMM, YYYY")}</span>}
        </div>
        <p className="lwp-question-text">{question.question}</p>
        {attachments.length > 0 && (
          <div className="lwp-file-chips on-dark">
            {attachments.map((url) => (
              <a key={url} href={url} target="_blank" rel="noreferrer" className="lwp-file-chip">
                <i className="bi bi-paperclip" /> {fileNameOf(url)}
              </a>
            ))}
          </div>
        )}
      </section>

      {myAnswer ? (
        <section className="lwp-card lwp-my-answer">
          <div className="lwp-my-answer-head">
            <h3>Your Answer</h3>
            {myAnswer.created_at && <time>{moment(myAnswer.created_at).format("DD MMM, YYYY hh:mm A")}</time>}
          </div>
          <p>{myAnswer.answer}</p>
        </section>
      ) : closed ? (
        <EmptyState icon="bi-lock" text="This question has been closed and no longer accepts answers." />
      ) : (
        <form className="lwp-answer-form" onSubmit={submit}>
          <label className="lwp-field">
            <span>Answer</span>
            <textarea
              className="lwp-textarea"
              rows={8}
              maxLength={MAX_ANSWER}
              placeholder="Write your answer..."
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
            />
          </label>
          <div className="lwp-answer-tools">
            <button type="button" className="lwp-ai-btn" onClick={writeWithAI} disabled={drafting}>
              {drafting ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-stars" />}
              Write with AI
            </button>
            <span className="lwp-counter">
              {answer.length}/{MAX_ANSWER}
            </span>
          </div>

          <label className="lwp-check">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
            <span>
              Accept all{" "}
              <a href={`${basePath}/privacy`} target="_blank" rel="noreferrer">
                Privacy policy
              </a>{" "}
              and{" "}
              <a href={`${basePath}/terms`} target="_blank" rel="noreferrer">
                Terms &amp; conditions
              </a>
              .
            </span>
          </label>

          <button type="submit" className="lwp-btn" disabled={submitting || !answer.trim() || !accepted}>
            {submitting ? <span className="spinner-border spinner-border-sm" /> : "Submit"}
          </button>
        </form>
      )}
    </div>
  );
};

export default LawyerQuestionAnswer;
